import { membersPagedQuery } from "@/api/supporter";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableFooter } from "@/components/data-table/DataTableFooter";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { titleCase } from "@common/utils/string";
import { QueryHandler, UnstyledButton } from "@givenwell/components";
import { Input } from "@/components/ui/Input";
import { ArrowSortAscIcon, ArrowSortDescIcon, ArrowSortIcon } from "@givenwell/core-solid-icons";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { fallback, number, object, optional, pipe, string, toMinValue } from "valibot";

export const Route = createFileRoute("/admin/supporters/$supporterId/")({
  component: SupporterMembersPage,
  validateSearch: object({
    page: fallback(pipe(optional(number(), 1), toMinValue(1)), 1),
    "page-size": fallback(pipe(optional(number(), 25), toMinValue(10)), 25),
    sort: fallback(optional(string()), undefined),
    query: fallback(optional(string()), undefined),
  }),
});

// Helper to parse sort string like "name:asc"
function parseSort(sort: string | undefined): { field: string; direction: "asc" | "desc" } | undefined {
  if (!sort) return undefined;
  const parts = sort.split(":");
  const field = parts[0];
  const dir = parts[1];
  if (!field) return undefined;
  return { field, direction: dir === "desc" ? "desc" : "asc" };
}

type SortableHeaderProps = {
  field: string;
  children: React.ReactNode;
  parsedSort: { field: string; direction: "asc" | "desc" } | undefined;
  onSort: (field: string) => void;
};

function SortableHeader({ field, children, parsedSort, onSort }: SortableHeaderProps) {
  const isActive = parsedSort?.field === field;
  const SortIcon =
    isActive && parsedSort?.direction === "asc"
      ? ArrowSortAscIcon
      : isActive && parsedSort?.direction === "desc"
        ? ArrowSortDescIcon
        : ArrowSortIcon;

  return (
    <UnstyledButton
      onClick={() => onSort(field)}
      css={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {children} <SortIcon width="11" height="11" color={colors.text2} />
    </UnstyledButton>
  );
}

function SupporterMembersPage() {
  const { supporterId } = Route.useParams();
  const { page, "page-size": pageSize, sort, query } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const [searchInput, setSearchInput] = useState(query ?? "");

  // Sync local state when URL query changes (e.g., browser back/forward)
  useEffect(() => {
    setSearchInput(query ?? "");
  }, [query]);

  // Debounce search - update URL after 300ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== (query ?? "")) {
        navigate({
          search: prev => ({ ...prev, query: trimmed || undefined, page: 1 }),
          replace: true,
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, query, navigate]);

  const parsedSort = parseSort(sort);

  const membersQuery = useQuery(membersPagedQuery(supporterId, page, pageSize, false, sort, query));

  const handleSort = (field: string) => {
    const currentSort = parseSort(sort);
    let newSort: string | undefined;

    if (currentSort?.field === field) {
      // Toggle direction or clear
      if (currentSort.direction === "asc") {
        newSort = `${field}:desc`;
      } else {
        newSort = undefined; // Clear sort
      }
    } else {
      newSort = `${field}:asc`;
    }

    navigate({
      search: prev => ({ ...prev, sort: newSort, page: 1 }),
      replace: true,
    });
  };

  return (
    <DataTable>
      <DataTableHeader hideClear hideToggle hideSearch>
        <Input
          placeholder="Search by name or email..."
          value={searchInput}
          onValueChange={setSearchInput}
          css={{ maxW: 300 }}
        />
      </DataTableHeader>
      <QueryHandler
        query={membersQuery}
        success={({ data }) => (
          <DataTableBody
            data={data.items}
            dataKey="supporter_member_id"
            emptyStateMessage="No team members found."
            columns={[
              {
                header: (
                  <SortableHeader field="name" parsedSort={parsedSort} onSort={handleSort}>
                    Name
                  </SortableHeader>
                ),
                cell: member => `${member.given_name} ${member.family_name}`,
              },
              {
                header: (
                  <SortableHeader field="email" parsedSort={parsedSort} onSort={handleSort}>
                    Email
                  </SortableHeader>
                ),
                cell: "email",
              },
              {
                header: "Invitation Status",
                // invitation_details is populated for platform admins (this page); null here means
                // no invitation row exists, not a redaction. `invited` says the same thing and is
                // the field the supporter-facing Staff table uses.
                cell: r => (r.invited ? titleCase(r.invitation_details?.status) : "Not invited"),
              },
            ]}
          />
        )}
      />
      <DataTableFooter itemCount={membersQuery.data?.total_items ?? 0} />
    </DataTable>
  );
}
